import React, { useEffect, useMemo } from "react";
import { classNames } from "primereact/utils";
import styles from "./BookingConfirmationStep.module.scss";
import { BookingDetails } from "./BookingDetails/BookingDetails";
import { IService } from "../../models/IService";
import { useAuthentication } from "../../../../../common/infrastructure/hooks/useAuthentication";
import { useAppContext } from "../../../../../AppContext";
import { useBookingConfirmationValidation } from "../../hooks/useBookingConfirmationValidation";
import { IEmployeeAvailability } from "../../models";
import { useNavigate } from "react-router";

interface IBookingConfirmationStepProps {
  className?: string;
  selectedService?: IService | null;
  selectedEmployee?: IEmployeeAvailability | null;
  selectedDate?: Date | null;
  selectedTime?: string | null;
  totalPrice?: number;
  isLoading?: boolean;
  onValidationChange?: (canProceed: boolean, validationMessage: string) => void;
}

export const BookingConfirmationStep = ({
  className,
  selectedService,
  selectedEmployee,
  selectedDate,
  selectedTime,
  totalPrice,
  isLoading = false,
  onValidationChange,
}: IBookingConfirmationStepProps) => {
  const { isAuthenticated } = useAuthentication();
  const { selectedStudio, currentUser } = useAppContext();
  const navigate = useNavigate();

  // Validation
  const { canProceed, validationMessage } = useBookingConfirmationValidation({
    selectedService,
    selectedEmployee,
    selectedDate,
    selectedTime,
    termsAccepted: true, // Always consider terms accepted since we removed the checkbox
    isAuthenticated,
  });

  // Notify parent component about validation changes
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(canProceed, validationMessage);
    }
  }, [canProceed, validationMessage, onValidationChange]);

  // Calculate booking details
  const bookingDetails = useMemo(() => {
    if (
      !selectedService ||
      !selectedEmployee ||
      !selectedDate ||
      !selectedTime
    ) {
      return null;
    }

    const formatDate = (date: Date): string => {
      const weekdays = [
        "неделя",
        "понеделник",
        "вторник",
        "сряда",
        "четвъртък",
        "петък",
        "събота",
      ];
      const weekday = weekdays[date.getDay()];
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${weekday}, ${day}.${month}.${year}`;
    };

    const formatTimeRange = (startTime: string, duration: number): string => {
      // Parse start time (assuming format "HH:MM")
      const [hours, minutes] = startTime.split(":").map(Number);
      const startDate = new Date();
      startDate.setHours(hours, minutes, 0, 0);

      // Calculate end time
      const endDate = new Date(startDate.getTime() + duration * 60000);

      // Format both times
      const formatTime = (date: Date) => {
        return `${String(date.getHours()).padStart(2, "0")}:${String(
          date.getMinutes()
        ).padStart(2, "0")}`;
      };

      return `${formatTime(startDate)}-${formatTime(endDate)}`;
    };

    return {
      service: selectedService,
      employee: selectedEmployee,
      date: formatDate(selectedDate),
      timeRange: formatTimeRange(selectedTime, selectedService.duration),
      price: totalPrice || selectedService.price,
      studio: selectedStudio,
      customer: currentUser,
    };
  }, [
    selectedService,
    selectedEmployee,
    selectedDate,
    selectedTime,
    totalPrice,
    selectedStudio,
    currentUser,
  ]);

  const isOAuthReturn = window.location.search.includes("oauth_return=true");
  const hasBackupData = localStorage.getItem("wizardStateBackup");

  useEffect(() => {
    if (
      !!currentUser?.phoneNumber &&
      bookingDetails &&
      !isLoading &&
      !(hasBackupData && isOAuthReturn)
    ) {
      setTimeout(() => {
        localStorage.removeItem("wizardStateBackup");
      }, 4000);
    }
  }, [isLoading, bookingDetails]);

  if (isLoading) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <h2 className={styles.title}>Детайли за резервацията</h2>
          <p className={styles.subtitle}>
            Моля, прегледайте детайлите на резервацията
          </p>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Подготвяне на резервацията...</p>
        </div>
      </div>
    );
  }

  if (hasBackupData && isOAuthReturn) {
    let errorMessage = "Възстановяване на данните...";
    let errorSubtext =
      "Моля, изчакайте докато възстановим информацията за вашата резервация";

    if (!isOAuthReturn && hasBackupData) {
      navigate(0);
    }

    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <h2 className={styles.title}>Детайли за резервацията</h2>
          <p className={styles.subtitle}>
            Моля, прегледайте детайлите на резервацията
          </p>
        </div>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>
            {isOAuthReturn && hasBackupData ? "⏳" : "⚠️"}
          </div>
          <p className={styles.errorText}>{errorMessage}</p>
          <p className={styles.errorSubtext}>{errorSubtext}</p>
        </div>
      </div>
    );
  }

  if (!bookingDetails) {
    return <></>;
  }

  return (
    <div className={classNames(styles.container, className)}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Детайли за резервацията</h2>
        <p className={styles.subtitle}>
          Моля, прегледайте детайлите на резервацията преди потвърждаване
        </p>
      </div>

      {/* Booking Details */}
      <BookingDetails
        bookingDetails={bookingDetails}
        className={styles.bookingDetails}
      />

      {/* Customer Information */}
      {isAuthenticated && currentUser ? (
        <div className={styles.customerSection}>
          <h3 className={styles.sectionTitle}>Информация за клиента</h3>
          <div className={styles.customerInfo}>
            <div className={styles.customerDetail}>
              <span className={styles.customerLabel}>Име:</span>
              <span className={styles.customerValue}>
                {currentUser.firstName} {currentUser.lastName}
              </span>
            </div>
            {(currentUser as any)?.email && (
              <div className={styles.customerDetail}>
                <span className={styles.customerLabel}>Имейл:</span>
                <span className={styles.customerValue}>
                  {(currentUser as any).email}
                </span>
              </div>
            )}
            {currentUser?.phoneNumber && (
              <div className={styles.customerDetail}>
                <span className={styles.customerLabel}>Телефон:</span>
                <span className={styles.customerValue}>
                  {currentUser.phoneNumber}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.authPrompt}>
          <div className={styles.authIcon}>👤</div>
          <h3 className={styles.authTitle}>Влезте в профила си</h3>
          <p className={styles.authText}>
            За да завършите резервацията, е необходимо да влезете в профила си
          </p>
        </div>
      )}

      {/* Important Notes */}
      <div className={styles.notesSection}>
        <h3 className={styles.sectionTitle}>Важна информация</h3>
        <div className={styles.notesList}>
          <div className={styles.noteItem}>
            <span className={styles.noteIcon}>📅</span>
            <span className={styles.noteText}>
              Моля, пристигнете поне 5 минути преди часа на резервацията
            </span>
          </div>
          <div className={styles.noteItem}>
            <span className={styles.noteIcon}>📱</span>
            <span className={styles.noteText}>
              Ще получите Email потвърждение след завършване на резервацията
            </span>
          </div>
          <div className={styles.noteItem}>
            <span className={styles.noteIcon}>⚡️</span>
            <span className={styles.noteText}>
              Можете да контролирате резервациитe си във Вашия профил.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
